import React, { useEffect, useState } from 'react';
import {
    Group,
    DropdownMenu,
    Text,
    List,
    CaretDownOutlined,
    CheckCircleFilled,
    Box,
    useBreakpoint,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { useLocation } from 'react-router-dom';
import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { FiltersParams } from 'types/params';
import useSortingOption, {
    SamplesTableSortingTypes,
    DropdownSortingTypes,
} from './useSortingOptionItem';

const sortingLabels: {
    [key in DropdownSortingTypes]: string;
} = {
    trending: t`Trending`,
    recent: t`Recent`,
    nameAsc: t`A - Z`,
    nameDesc: t`Z - A`,
};

const sortingSamplesTableLabels: {
    [key in SamplesTableSortingTypes]: string;
} = {
    name: t`Name`,
    time: t`Time`,
    type: t`Type`,
    bpm: t`BPM`,
    key: t`Key`,
};

const SortingItem = ({
    type,
}: {
    isDefault?: boolean;
    type: DropdownSortingTypes;
}) => {
    const { callback, isActive } = useSortingOption({
        type,
    });

    return (
        <DropdownMenu.Item
            onClick={() => callback()}
            data-testid={`button-${sortingLabels[type]}`}
        >
            {sortingLabels[type]}
            {isActive && (
                <List.ItemSecondaryAction>
                    <CheckCircleFilled size="sm" />
                </List.ItemSecondaryAction>
            )}
        </DropdownMenu.Item>
    );
};

export const getActiveSortingType = ({
    orderby,
    orderbydirection,
}: {
    orderby: string | null;
    orderbydirection: string | null;
}): DropdownSortingTypes | undefined => {
    const isOrderByName = orderby?.toLocaleLowerCase() === 'name';
    const isAscending = orderbydirection?.toLocaleLowerCase() === 'ascending';
    const isDescending = orderbydirection?.toLocaleLowerCase() === 'descending';
    const isOrderby = orderby;

    if (isOrderByName && isAscending) {
        return DropdownSortingTypes.NameAsc;
    }
    if (isOrderByName && isDescending) {
        return DropdownSortingTypes.NameDesc;
    } else if (isOrderby) {
        return orderby as DropdownSortingTypes;
    } else {
        return undefined;
    }
};

const DropdownSorting = ({
    sortingOptions,
}: {
    sortingOptions: DropdownSortingTypes[];
}) => {
    const isMobile = useBreakpoint('md');
    const { orderby, orderbydirection } = useCurrentSearchState();
    const { search } = useLocation();
    const [isPacksTable, setIsPacksTable] = useState(false);
    const { clearByFilter } = useSearchModifiers();

    useEffect(
        function resetSortingList() {
            const wasPacksTable = isPacksTable;
            const isNowPacksTable = search.toLowerCase().includes('tab=packs');

            setIsPacksTable(isNowPacksTable);

            if (wasPacksTable && !isNowPacksTable) {
                clearByFilter(FiltersParams.orderby);
                clearByFilter(FiltersParams.orderbydirection);
            }
        },
        [clearByFilter, search, isPacksTable],
    );

    const activeSortingType = getActiveSortingType({
        orderby,
        orderbydirection,
    });

    const activeOption = activeSortingType && sortingLabels[activeSortingType];
    const activeSamplesTableOption =
        orderby &&
        !isPacksTable &&
        sortingSamplesTableLabels[orderby as SamplesTableSortingTypes];
    const activeOptionLabel = activeSamplesTableOption
        ? activeSamplesTableOption
        : activeOption;
    const firstOption = sortingOptions[0];
    const defaultOption = sortingLabels[firstOption];

    return (
        <>
            <Group gap="xs">
                <Group.Item>
                    {!isMobile && (
                        <Text color="text.medium" textStyle="body">
                            <Trans>Sort by</Trans>
                        </Text>
                    )}
                </Group.Item>
                <Group.Item>
                    <DropdownMenu placement="bottom-end" size="xs">
                        <DropdownMenu.Trigger>
                            <Box cursor="pointer">
                                <Group gap="xs" alignContent={'center'}>
                                    <Group.Item>
                                        <Text
                                            fontWeight="bold"
                                            textStyle="body"
                                        >
                                            {activeOptionLabel ?? defaultOption}
                                        </Text>
                                    </Group.Item>
                                    <Group.Item>
                                        <CaretDownOutlined />
                                    </Group.Item>
                                </Group>
                            </Box>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.List
                            width={{ base: 'auto', md: '200px' }}
                            minWidth={{ base: 'auto', md: '200px' }}
                        >
                            {sortingOptions
                                ?.slice(0, isPacksTable ? undefined : -2)
                                .map((sortingOption) => (
                                    <SortingItem
                                        key={`sortingOption-${sortingOption}`}
                                        type={sortingOption}
                                    />
                                ))}
                        </DropdownMenu.List>
                    </DropdownMenu>
                </Group.Item>
            </Group>
        </>
    );
};

DropdownSorting.displayName = 'DetailsView.DropdownSorting';

export default DropdownSorting;
